import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Calendar, MessageSquare, RefreshCw, Mail, Phone } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDate = (iso) => {
  try { return new Date(iso).toLocaleString('de-DE'); } catch { return iso; }
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('bookings');
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);

  const authHeaders = () => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin'); return {}; }
    return { Authorization: `Bearer ${token}` };
  };

  const load = async () => {
    setLoading(true);
    try {
      const headers = authHeaders();
      const [b, c] = await Promise.all([
        axios.get(`${API}/admin/bookings`, { headers }),
        axios.get(`${API}/admin/contacts`, { headers }),
      ]);
      setBookings(b.data || []);
      setContacts(c.data || []);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin');
      } else {
        toast.error('Fehler beim Laden');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const logout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/admin');
  };

  const user = localStorage.getItem('admin_user') || 'admin';

  return (
    <div className="bg-charcoal min-h-screen text-zinc-100">
      <header className="border-b border-zinc-800 bg-[#1c1c1c]">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="font-display text-yellow text-xl">LLIT-Media — Admin</h1>
            <p className="text-zinc-500 text-xs">Eingeloggt als {user}</p>
          </div>
          <div className="flex gap-2">
            <button onClick={load} className="btn-dark py-2 px-4 text-sm" disabled={loading}>
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Aktualisieren
            </button>
            <button onClick={logout} className="btn-dark py-2 px-4 text-sm">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setTab('bookings')}
            className={`px-4 py-2 rounded-md font-heavy text-sm flex items-center gap-2 ${tab==='bookings' ? 'bg-yellow text-[#1a1a1a]' : 'bg-[#252525] text-zinc-300 hover:text-yellow'}`}
          >
            <Calendar className="w-4 h-4" /> Buchungen ({bookings.length})
          </button>
          <button
            onClick={() => setTab('contacts')}
            className={`px-4 py-2 rounded-md font-heavy text-sm flex items-center gap-2 ${tab==='contacts' ? 'bg-yellow text-[#1a1a1a]' : 'bg-[#252525] text-zinc-300 hover:text-yellow'}`}
          >
            <MessageSquare className="w-4 h-4" /> Anfragen ({contacts.length})
          </button>
        </div>

        {tab === 'bookings' ? (
          <div className="space-y-3">
            {bookings.length === 0 && <p className="text-zinc-500 text-center py-12">Noch keine Buchungen.</p>}
            {bookings.map((b) => (
              <div key={b.id} className="bg-[#252525] rounded-lg p-5 border border-zinc-800">
                <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
                  <div className="flex items-center gap-3">
                    <span className="font-heavy text-yellow text-lg">{b.date}</span>
                    <span className="font-semibold text-white">{b.name}</span>
                  </div>
                  <span className="text-zinc-500 text-xs">gebucht am {formatDate(b.created_at)}</span>
                </div>
                <div className="grid sm:grid-cols-2 gap-2 text-sm text-zinc-300">
                  <a href={`mailto:${b.email}`} className="flex items-center gap-2 hover:text-yellow"><Mail className="w-4 h-4" />{b.email}</a>
                  {b.phone && <a href={`tel:${b.phone}`} className="flex items-center gap-2 hover:text-yellow"><Phone className="w-4 h-4" />{b.phone}</a>}
                </div>
                {b.service && <p className="text-sm text-zinc-400 mt-2"><span className="text-yellow">Service:</span> {b.service}</p>}
                {b.message && <p className="text-sm text-zinc-300 mt-2 whitespace-pre-wrap border-l-2 border-yellow pl-3">{b.message}</p>}
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {contacts.length === 0 && <p className="text-zinc-500 text-center py-12">Noch keine Anfragen.</p>}
            {contacts.map((c) => (
              <div key={c.id} className="bg-[#252525] rounded-lg p-5 border border-zinc-800">
                <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
                  <span className="font-semibold text-white">{c.first} {c.last || ''}</span>
                  <span className="text-zinc-500 text-xs">{formatDate(c.created_at)}</span>
                </div>
                <div className="grid sm:grid-cols-2 gap-2 text-sm text-zinc-300 mb-2">
                  <a href={`mailto:${c.email}`} className="flex items-center gap-2 hover:text-yellow"><Mail className="w-4 h-4" />{c.email}</a>
                  {c.phone && <a href={`tel:${c.phone}`} className="flex items-center gap-2 hover:text-yellow"><Phone className="w-4 h-4" />{c.phone}</a>}
                </div>
                {c.services?.length > 0 && <p className="text-sm text-zinc-400"><span className="text-yellow">Services:</span> {c.services.join(', ')}</p>}
                {c.other && <p className="text-sm text-zinc-300 mt-2 border-l-2 border-yellow pl-3"><span className="text-yellow font-semibold">Sonstiges: </span>{c.other}</p>}
                {c.message && <p className="text-sm text-zinc-300 mt-2 whitespace-pre-wrap border-l-2 border-zinc-600 pl-3">{c.message}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
